import { useCallback, useEffect, useRef, useState } from "react";

import { encodeGeohash } from "utils/geohash";

const DUBLIN_CENTER = {
  lat: 53.349804,
  lng: -6.26031,
};

const GEOHASH_PRECISION = 9;

const EIRCODE_PATTERN = /[A-Z]\d{2}\s?[A-Z0-9]{4}/;

/*
 * Google's JavaScript API returns coordinates as LatLng objects whose lat/lng are
 * functions; the same shape arrives as plain numbers from a picked place.
 */
const readLatLng = (location) => ({
  lat: typeof location.lat === "function" ? location.lat() : location.lat,
  lng: typeof location.lng === "function" ? location.lng() : location.lng,
});

/*
 * The two geocoding outcomes the user can actually act on. Every other status —
 * REQUEST_DENIED, OVER_QUERY_LIMIT, a failed request — is about our Google key or the
 * network, so it is logged for whoever debugs it and reported as "the map is not
 * available" rather than as a code the user is expected to interpret.
 */
const ADDRESS_STATUS_MESSAGES = {
  ZERO_RESULTS: "No results found for this address. Please check and try again.",
  INVALID_REQUEST: "Invalid address format. Please enter a valid address.",
};

/**
 * The Google side of the location tab: geocoding the practice's address, the initial
 * pin, and the marker drag that writes coordinates (and an eircode, when Google can
 * name one) back into the form.
 */
export const usePracticeLocationMap = ({
  mapsScript,
  eircode,
  fullAddress,
  initialLatitude,
  initialLongitude,
  initialGeoHash,
  onLocationChange,
  onEircodeChange,
}) => {
  const [coordinates, setCoordinates] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isWaitingForMapsScript, setIsWaitingForMapsScript] = useState(false);
  const [addressMessage, setAddressMessage] = useState("");
  const [hasMapFailed, setHasMapFailed] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const pendingAddressRef = useRef(null);

  /*
   * The raw reason stays in the console for whoever debugs it; the user is told the
   * map is unavailable, not the status code. Anything still queued for the geocoder
   * is dropped, so the screen cannot claim to be searching and to have failed at once.
   */
  const reportMapFailure = useRef((reason) => {
    console.error("Practice location map unavailable:", reason);
    pendingAddressRef.current = null;
    setIsWaitingForMapsScript(false);
    setIsLoading(false);
    setHasMapFailed(true);
  }).current;

  /*
   * Google reports a rejected key by calling this global rather than by failing the
   * script load, and without it the map paints its own grey "Oops!" panel.
   */
  useEffect(() => {
    const previousHandler = window.gm_authFailure;

    window.gm_authFailure = () => {
      reportMapFailure("Google Maps authentication failed (gm_authFailure)");
      previousHandler?.();
    };

    return () => {
      window.gm_authFailure = previousHandler;
    };
  }, [reportMapFailure]);

  const geocodeAddress = useCallback(
    async (addressToGeocode) => {
      if (!addressToGeocode || addressToGeocode.trim() === "") {
        return null;
      }

      const GeocoderApi = window.google?.maps?.Geocoder;

      /*
       * Forward geocoding goes through the Maps JavaScript API, the same one the
       * marker drag already used: the REST Geocoding web service rejects this key
       * outright, because keys with HTTP-referrer restrictions are not accepted
       * there in any environment. The JavaScript API accepts them.
       *
       * It only exists once the Maps script has loaded, so an address asked for
       * before then is held and looked up when the script reports ready, rather
       * than quietly dropped.
       */
      if (!GeocoderApi) {
        // Nothing to wait for if the script is never going to arrive.
        if (mapsScript.hasFailed) {
          reportMapFailure("Google Maps script unavailable");

          return null;
        }

        pendingAddressRef.current = addressToGeocode;
        setIsWaitingForMapsScript(true);

        return null;
      }

      setIsLoading(true);
      setAddressMessage("");

      try {
        const { status, results } = await new Promise((resolve) => {
          new GeocoderApi().geocode(
            { address: addressToGeocode },
            (geocodeResults, geocodeStatus) =>
              resolve({ status: geocodeStatus, results: geocodeResults })
          );
        });

        if (status === "OK" && results && results.length > 0) {
          // The JavaScript API hands back LatLng objects, not plain numbers.
          const location = readLatLng(results[0].geometry.location);
          const geoHash = encodeGeohash(
            location.lat,
            location.lng,
            GEOHASH_PRECISION
          );

          setCoordinates(location);
          onLocationChange({
            ...location,
            geoHash,
          });
          setAddressMessage("");

          return { ...location, geoHash };
        }

        if (ADDRESS_STATUS_MESSAGES[status]) {
          setAddressMessage(ADDRESS_STATUS_MESSAGES[status]);

          return null;
        }

        reportMapFailure(`Geocoding failed: ${status}`);

        return null;
      } catch (error) {
        reportMapFailure(error);

        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [mapsScript.hasFailed, onLocationChange, reportMapFailure]
  );

  /*
   * An address held back because the script was not ready yet is looked up the moment
   * it is, and abandoned with a reported failure if the script never arrives.
   */
  useEffect(() => {
    if (!mapsScript.isReady) return;

    setIsWaitingForMapsScript(false);

    const pendingAddress = pendingAddressRef.current;
    pendingAddressRef.current = null;

    if (pendingAddress) geocodeAddress(pendingAddress);
  }, [mapsScript.isReady, geocodeAddress]);

  useEffect(() => {
    if (mapsScript.hasFailed) {
      reportMapFailure("Google Maps script unavailable");
    }
  }, [mapsScript.hasFailed, reportMapFailure]);

  useEffect(() => {
    if (isInitialized) return;

    const initializeMap = async () => {
      if (initialLatitude && initialLongitude) {
        const latitude = parseFloat(initialLatitude);
        const longitude = parseFloat(initialLongitude);

        if (!isNaN(latitude) && !isNaN(longitude)) {
          const initialCoordinates = { lat: latitude, lng: longitude };
          setCoordinates(initialCoordinates);

          if (!initialGeoHash) {
            const geoHash = encodeGeohash(
              latitude,
              longitude,
              GEOHASH_PRECISION
            );
            onLocationChange({ ...initialCoordinates, geoHash });
          }

          setIsInitialized(true);

          return;
        }
      }

      if (fullAddress && fullAddress.trim() !== "") {
        const result = await geocodeAddress(fullAddress);
        if (result) {
          setIsInitialized(true);

          return;
        }
      }

      if (eircode && eircode.trim() !== "") {
        const result = await geocodeAddress(eircode);
        if (result) {
          setIsInitialized(true);

          return;
        }
      }

      setCoordinates(DUBLIN_CENTER);
      const geoHash = encodeGeohash(
        DUBLIN_CENTER.lat,
        DUBLIN_CENTER.lng,
        GEOHASH_PRECISION
      );
      onLocationChange({ ...DUBLIN_CENTER, geoHash });
      setIsInitialized(true);
    };

    initializeMap();
  }, [
    initialLatitude,
    initialLongitude,
    initialGeoHash,
    fullAddress,
    eircode,
    geocodeAddress,
    onLocationChange,
    isInitialized,
  ]);

  useEffect(() => {
    if (!isInitialized) return;

    if (fullAddress && fullAddress.trim() !== "") {
      geocodeAddress(fullAddress);
    } else if (eircode && eircode.trim() !== "") {
      geocodeAddress(eircode);
    }
  }, [fullAddress, eircode, geocodeAddress, isInitialized]);

  const applyEircodeFromPlace = useCallback(
    (place) => {
      const postalCodeComponent = place.address_components?.find((component) =>
        component.types.includes("postal_code")
      );

      if (postalCodeComponent) {
        onEircodeChange(postalCodeComponent.short_name);

        return;
      }

      const eircodeMatch = place.formatted_address.match(EIRCODE_PATTERN);

      if (eircodeMatch) {
        onEircodeChange(eircodeMatch[0]);
      }
    },
    [onEircodeChange]
  );

  const handleMarkerDragEnd = useCallback(
    (event) => {
      const newCoordinates = readLatLng(event.latLng);

      const geoHash = encodeGeohash(
        newCoordinates.lat,
        newCoordinates.lng,
        GEOHASH_PRECISION
      );

      setCoordinates(newCoordinates);
      onLocationChange({
        ...newCoordinates,
        geoHash,
      });

      if (window?.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();

        geocoder.geocode({ location: newCoordinates }, (results, status) => {
          if (status === "OK" && results && results.length > 0) {
            applyEircodeFromPlace(results[0]);
          } else {
            console.error("Reverse geocoding failed:", status);
          }
        });
      }
    },
    [onLocationChange, applyEircodeFromPlace]
  );

  return {
    coordinates,
    isLocating: isLoading || isWaitingForMapsScript,
    addressMessage,
    isMapUnavailable: hasMapFailed,
    handleMarkerDragEnd,
  };
};
